import { Link } from 'react-router-dom';
import { Phone, Mail, MessageSquare, Heart } from 'lucide-react';
import { Logo } from '@/components/common/Logo';

const CONTACT = {
  phone: '+91 74878 71017',
  phoneHref: 'tel:+917487871017',
  email: 'dreamoraa1502@gmail.com',
  emailHref: 'mailto:dreamoraa1502@gmail.com',
};

interface FooterProps {
  supportPath?: string;
}

export const Footer = ({ supportPath = '/support' }: FooterProps) => {
  return (
    <footer className="mt-auto border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">

          {/* Col 1 — Brand */}
          <div className="lg:col-span-1 flex flex-col gap-4">
            <Logo variant="full" size="sm" linkToHome={false} />
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Light up your living
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              Your trusted marketplace for home services and products across India.
            </p>
          </div>

          {/* Col 2 — Quick Links */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {[
                { to: '/dashboard', label: 'Dashboard' },
                { to: '/products', label: 'Products' },
                { to: '/services', label: 'Services' },
                { to: '/orders', label: 'My Orders' },
                { to: supportPath, label: 'Support' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors hover:underline underline-offset-2"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Contact + App */}
          <div className="flex flex-col gap-6">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                Contact Us
              </h3>
              <ul className="space-y-3 mb-4">
                <li>
                  <a
                    href={CONTACT.phoneHref}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Phone className="h-4 w-4 shrink-0 text-primary" />
                    {CONTACT.phone}
                  </a>
                </li>
                <li>
                  <a
                    href={CONTACT.emailHref}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Mail className="h-4 w-4 shrink-0 text-primary" />
                    {CONTACT.email}
                  </a>
                </li>
              </ul>
              <Link
                to={supportPath}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm px-4 py-2 rounded-full font-medium hover:opacity-90 transition-opacity"
              >
                <MessageSquare className="h-4 w-4" />
                Get Support
              </Link>
            </div>

            {/* App Download */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                Get the App
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { href: 'https://play.google.com/store/apps/details?id=com.dreamoraa.userapp', label: 'Customer App' },
                  { href: 'https://play.google.com/store/apps/details?id=com.dreamoraaPartner.app', label: 'Partner App' },
                ].map(({ href, label }) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 rounded-lg border bg-muted/50 hover:bg-muted px-3 py-2 transition-colors w-fit"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-foreground" fill="currentColor">
                      <path d="M3.18 23.76c.32.18.68.24 1.05.17l12.76-7.37-2.76-2.76-11.05 9.96zM.5 1.26C.19 1.6 0 2.1 0 2.73v18.54c0 .63.19 1.13.5 1.47l.08.07 10.39-10.39v-.24L.58 1.19.5 1.26zM20.1 10.28l-2.67-1.54-3.02 3.02 3.02 3.02 2.69-1.55c.77-.44.77-1.52-.02-1.95zM4.23.07L16.99 7.44 14.23 10.2 3.18.24C3.5.07 3.9.01 4.23.07z"/>
                    </svg>
                    <div>
                      <p className="text-[10px] text-muted-foreground leading-none">GET IT ON</p>
                      <p className="text-xs font-semibold text-foreground leading-snug">Google Play · {label}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-muted-foreground">
            © 2026 Dreamoraa. All rights reserved.
          </p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            Made with <Heart className="h-3.5 w-3.5 text-primary fill-primary" /> in India
          </p>
        </div>
      </div>
    </footer>
  );
};
